import React from 'react';
import { Building2, MapPin, DollarSign, Archive, Trash2, MoreVertical } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ConfirmationPopup from '../ui/ConfirmationPopup';
import type { Job } from '../../types/job';

interface JobListProps {
  jobs: Job[];
  onViewJob: (job: Job) => void;
  onEditJob: (job: Job) => void;
  onRefresh: () => void;
}

export default function JobList({ jobs, onViewJob, onEditJob, onRefresh }: JobListProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState<string | null>(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = React.useState<string | null>(null);
  const [showActions, setShowActions] = React.useState<string | null>(null);

  const handleDelete = async (jobId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('nrm_jobs')
        .delete()
        .eq('id', jobId);

      if (deleteError) throw deleteError;
      onRefresh();
    } catch (err) {
      console.error('Failed to delete job:', err);
    }
    setShowDeleteConfirm(null);
  };

  const handleArchive = async (jobId: string) => {
    try {
      const { error: updateError } = await supabase
        .from('nrm_jobs')
        .update({ 
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (updateError) throw updateError;
      onRefresh();
    } catch (err) {
      console.error('Failed to archive job:', err);
    }
    setShowArchiveConfirm(null);
  };

  return (
    <>
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">Position</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Company</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Location</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Salary Range</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {jobs.map((job) => (
                <tr 
                  key={job.id}
                  className="hover:bg-dark-700/50 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4" onClick={() => onViewJob(job)}>
                    <div className="font-medium">{job.position}</div>
                  </td>
                  <td className="px-6 py-4" onClick={() => onViewJob(job)}>
                    <div className="flex items-center text-gray-400">
                      <Building2 className="w-4 h-4 mr-2" />
                      {job.company?.name}
                    </div>
                  </td>
                  <td className="px-6 py-4" onClick={() => onViewJob(job)}>
                    {job.location && (
                      <div className="flex items-center text-gray-400">
                        <MapPin className="w-4 h-4 mr-2" />
                        {job.location}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4" onClick={() => onViewJob(job)}>
                    {job.salary_range && (
                      <div className="flex items-center text-gray-400">
                        <DollarSign className="w-4 h-4 mr-2" />
                        {job.salary_range}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4" onClick={() => onViewJob(job)}>
                    <span className="capitalize text-gray-400">
                      {job.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setShowActions(showActions === job.id ? null : job.id)}
                        className="p-2 hover:bg-dark-600 rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {showActions === job.id && (
                        <div className="fixed transform translate-y-2 right-4 w-48 bg-dark-800 rounded-lg shadow-lg py-1 z-50">
                          {job.status !== 'archived' && (
                            <button
                              onClick={() => {
                                setShowActions(null);
                                setShowArchiveConfirm(job.id);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-dark-700 flex items-center gap-2"
                            >
                              <Archive className="w-4 h-4" />
                              Archive
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setShowActions(null);
                              setShowDeleteConfirm(job.id);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-dark-700 text-red-500 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Forever
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showDeleteConfirm && (
        <ConfirmationPopup
          title="Delete Opportunity"
          message="Are you sure you want to delete this opportunity? This action cannot be undone and will remove all associated data."
          confirmLabel="Delete Forever"
          onConfirm={() => handleDelete(showDeleteConfirm)}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}

      {showArchiveConfirm && (
        <ConfirmationPopup
          title="Archive Opportunity"
          message="Are you sure you want to archive this opportunity? You can still view it but it will be removed from active opportunities."
          confirmLabel="Archive"
          onConfirm={() => handleArchive(showArchiveConfirm)}
          onCancel={() => setShowArchiveConfirm(null)}
        />
      )}
    </>
  );
}